import type { Node, Edge } from '../../../core/models/scene';
import type { OrgChartViewpoint } from '../../../stores/orgchartViewStore';

export type TenureBucket = {
  id: string;
  label: string;
  minMonths: number;
  maxMonths?: number;
  color: string;
};

export type TenureResult = {
  bucket: TenureBucket;
  months: number;
};

export interface OrgChartProjectionSettings {
  asOfDate?: string | null;
  activeViewpoint?: OrgChartViewpoint;
}

export interface OrgChartProjectionResult {
  nodes: Node[];
  edges: Edge[];
  tenure: Map<string, TenureResult>;
}

export const TENURE_BUCKETS: TenureBucket[] = [
  { id: 'lt-1y', label: '< 1y', minMonths: 0, maxMonths: 12, color: '#93c5fd' },
  { id: '1-2y', label: '1-2y', minMonths: 12, maxMonths: 24, color: '#6ee7b7' },
  { id: '2-5y', label: '2-5y', minMonths: 24, maxMonths: 60, color: '#fcd34d' },
  { id: '5y-plus', label: '5y+', minMonths: 60, color: '#fca5a5' },
];

const ORG_NODE_TYPES = new Set(['org_person', 'org_person_photo', 'org_person_photo_left', 'org_person_photo_right', 'org_person_photo_inner_left', 'org_person_text', 'org_position', 'org_department']);

const ORG_EDGE_TYPES = new Set([
  // Runtime edge types
  'reporting',
  'dotted',
  'matrix',
  'team',
  'occupies',
  // Relationship subtypes
  'reports_to',
  'assistant_to',
  'dotted_line_reports',
  'matrix_reports',
  'team_member',
]);

// Parse ISO-ish date strings; return null for empty/invalid values.
function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const parsedLocal = new Date(year, month - 1, day);
    if (Number.isNaN(parsedLocal.getTime())) return null;
    return parsedLocal;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

// Check if an edge is active at a specific date using attrs.validFrom/validTo.
function isEdgeActive(edge: Edge, asOf: Date): boolean {
  const attrs = (edge.attrs || {}) as Record<string, unknown>;
  const validFrom = parseDate(attrs.validFrom as string | undefined);
  if (validFrom && asOf < validFrom) return false;
  const validTo = parseDate(attrs.validTo as string | undefined);
  if (validTo && asOf > validTo) return false;
  return true;
}

// Completed month delta used for tenure buckets with exact date boundaries.
function monthsBetween(start: Date, end: Date): number {
  const years = end.getFullYear() - start.getFullYear();
  const months = end.getMonth() - start.getMonth();
  let total = years * 12 + months;

  // Count completed months only so bucket transitions happen on exact dates.
  if (end.getDate() < start.getDate()) {
    total -= 1;
  }

  if (total < 0) return 0;
  return total;
}

// Map a tenure duration to the first matching bucket.
function getTenureBucket(months: number): TenureBucket | null {
  for (const bucket of TENURE_BUCKETS) {
    if (months >= bucket.minMonths && (bucket.maxMonths === undefined || months < bucket.maxMonths)) {
      return bucket;
    }
  }
  return null;
}

// Gate projection to org chart nodes only.
function isOrgNode(node: Node): boolean {
  return ORG_NODE_TYPES.has(node.type);
}

// Gate projection to org chart edges, including relation-mapped edges.
function isOrgEdge(edge: Edge): boolean {
  if (ORG_EDGE_TYPES.has(edge.type)) return true;
  const relation = (edge.attrs as Record<string, unknown> | undefined)?.relation;
  return typeof relation === 'string' && ORG_EDGE_TYPES.has(relation);
}

// Extract a person's join date from node attrs.
function getNodeJoinDate(node: Node): Date | null {
  const attrs = (node.attrs || {}) as Record<string, unknown>;
  const props = (node.props || {}) as Record<string, unknown>;
  return parseDate((attrs.joinDate as string | undefined) ?? (props.joinDate as string | undefined));
}

// Extract a person's departure date from node attrs.
function getNodeDepartureDate(node: Node): Date | null {
  const attrs = (node.attrs || {}) as Record<string, unknown>;
  const props = (node.props || {}) as Record<string, unknown>;
  return parseDate((attrs.departureDate as string | undefined) ?? (props.departureDate as string | undefined));
}

// Extract the start date from edge attrs.
function getEdgeValidFrom(edge: Edge): Date | null {
  const attrs = (edge.attrs || {}) as Record<string, unknown>;
  return parseDate(attrs.validFrom as string | undefined);
}

export class OrgChartPerspectiveProjection {
  private static prevKey: string | null = null;
  private static prevResult: OrgChartProjectionResult | null = null;
  private static prevNodesRef: Node[] | null = null;
  private static prevEdgesRef: Edge[] | null = null;
  private static prevSettingsRef: OrgChartProjectionSettings | null = null;

  // Build a stable signature for memoizing projection results.
  private static buildSignature(nodes: Node[], edges: Edge[], settings: OrgChartProjectionSettings): string {
    const nodeSig = nodes
      .map((n) => {
        const attrs = (n.attrs || {}) as Record<string, unknown>;
        const props = (n as any).props as Record<string, unknown> | undefined;
        const fillColor = (n as any).fillColor ?? attrs.fillColor ?? props?.fillColor;
        const strokeColor = (n as any).strokeColor ?? attrs.strokeColor ?? props?.strokeColor;
        const textColor = (attrs.textColor as string | undefined) ?? (props?.textColor as string | undefined);
        const zIndex = Number((n as any).zIndex ?? 0);
        return [
          n.id,
          n.type,
          n.x,
          n.y,
          n.w,
          n.h,
          zIndex,
          attrs.joinDate,
          attrs.name,
          attrs.role,
          attrs.title,
          attrs.department,
          attrs.personId,
          attrs.email,
          attrs.photoRef,
          attrs.unstaffed,
          attrs.customFields,
          attrs.visibleFields,
          attrs.accentColor,
          fillColor,
          strokeColor,
          textColor,
          attrs.subtitleColor,
          attrs.fontWeight,
          attrs.fontStyle,
          attrs.textDecoration,
        ];
      })
      .sort((a, b) => (a[0] as string).localeCompare(b[0] as string));

    const edgeSig = edges
      .map((e) => {
        const attrs = (e.attrs || {}) as Record<string, unknown>;
        return [
          e.id,
          e.type,
          e.from,
          e.to,
          attrs.validFrom,
          attrs.validTo,
          attrs.relation,
          // Include styling attrs to invalidate cache when they change
          attrs.routingType,
          attrs.color,
          attrs.dashed,
          attrs.width,
          JSON.stringify(attrs.endArrow),
          JSON.stringify(attrs.startArrow),
          attrs.label,
        ];
      })
      .sort((a, b) => (a[0] as string).localeCompare(b[0] as string));

    return JSON.stringify({ nodeSig, edgeSig, settings });
  }

  // Compute filtered nodes/edges + tenure map (view-only projection).
  static compute(nodes: Node[], edges: Edge[], settings: OrgChartProjectionSettings): OrgChartProjectionResult {
    // Fast path: reference equality with value-check on settings (inline object each call)
    if (
      this.prevResult &&
      this.prevNodesRef === nodes &&
      this.prevEdgesRef === edges &&
      this.prevSettingsRef?.asOfDate === settings.asOfDate &&
      this.prevSettingsRef?.activeViewpoint === settings.activeViewpoint
    ) {
      return this.prevResult;
    }

    const asOf = parseDate(settings.asOfDate || undefined);
    const activeViewpoint = settings.activeViewpoint ?? 'none';

    // Short-circuit: when no date filter and no viewpoint, result is identity pass-through.
    // This avoids the expensive O(n log n) buildSignature() for the common case.
    if (!asOf && activeViewpoint === 'none') {
      const result: OrgChartProjectionResult = { nodes, edges, tenure: new Map() };
      this.prevKey = null;
      this.prevNodesRef = nodes;
      this.prevEdgesRef = edges;
      this.prevSettingsRef = settings;
      this.prevResult = result;
      return result;
    }

    const key = this.buildSignature(nodes, edges, settings);
    if (this.prevKey === key && this.prevResult) {
      return this.prevResult;
    }

    let filteredEdges = edges;
    if (asOf) {
      filteredEdges = edges.filter((edge) => {
        if (!isOrgEdge(edge)) return true;
        return isEdgeActive(edge, asOf);
      });
    }

    let filteredNodes = nodes;
    if (asOf) {
      filteredNodes = nodes.filter((node) => {
        if (!isOrgNode(node)) return true;
        if (node.type !== 'org_person' && node.type !== 'org_person_photo' && node.type !== 'org_person_photo_left' && node.type !== 'org_person_photo_right' && node.type !== 'org_person_photo_inner_left' && node.type !== 'org_person_text') return true;
        const joinDate = getNodeJoinDate(node);
        if (joinDate && joinDate > asOf) return false;

        const departureDate = getNodeDepartureDate(node);
        if (departureDate && departureDate < asOf) return false;

        return true;
      });

      const visibleNodeIds = new Set(filteredNodes.map((node) => node.id));
      filteredEdges = filteredEdges.filter((edge) => visibleNodeIds.has(edge.from) && visibleNodeIds.has(edge.to));
    }

    const tenure = new Map<string, TenureResult>();
    if (activeViewpoint === 'tenure') {
      const nodesById = new Map(filteredNodes.map((node) => [node.id, node] as const));
      const edgesByPosition = new Map<string, Edge[]>();

      filteredEdges.forEach((edge) => {
        const relation = (edge.attrs as Record<string, unknown> | undefined)?.relation;
        const edgeType = relation === 'occupies' ? 'occupies' : edge.type;
        if (edgeType !== 'occupies') return;
        if (!edgesByPosition.has(edge.to)) edgesByPosition.set(edge.to, []);
        edgesByPosition.get(edge.to)!.push(edge);
      });

      const endDate = asOf ?? new Date();

      filteredNodes.forEach((node) => {
        if (!isOrgNode(node)) return;
        if (node.type === 'org_department') return;

        let startDate: Date | null = null;

        if (node.type === 'org_position') {
          const occupiesEdges = edgesByPosition.get(node.id) || [];
          const activeEdge = occupiesEdges.find((edge) => {
            if (!asOf) return true;
            return isEdgeActive(edge, asOf);
          });
          if (activeEdge) {
            startDate = getEdgeValidFrom(activeEdge);
            if (!startDate) {
              const person = nodesById.get(activeEdge.from);
              if (person && (person.type === 'org_person' || person.type === 'org_person_photo' || person.type === 'org_person_photo_left' || person.type === 'org_person_photo_right' || person.type === 'org_person_photo_inner_left' || person.type === 'org_person_text')) {
                startDate = getNodeJoinDate(person);
              }
            }
          }
        } else if (node.type === 'org_person' || node.type === 'org_person_photo' || node.type === 'org_person_photo_left' || node.type === 'org_person_photo_right' || node.type === 'org_person_photo_inner_left' || node.type === 'org_person_text') {
          startDate = getNodeJoinDate(node);
          if (!startDate) {
            const occupiesEdge = filteredEdges.find((edge) => {
              const relation = (edge.attrs as Record<string, unknown> | undefined)?.relation;
              const edgeType = relation === 'occupies' ? 'occupies' : edge.type;
              return edgeType === 'occupies' && edge.from === node.id;
            });
            if (occupiesEdge) startDate = getEdgeValidFrom(occupiesEdge);
          }
        }

        if (!startDate) return;
        const months = monthsBetween(startDate, endDate);
        const bucket = getTenureBucket(months);
        if (!bucket) return;
        tenure.set(node.id, { bucket, months });
      });
    }

    const result: OrgChartProjectionResult = {
      nodes: filteredNodes,
      edges: filteredEdges,
      tenure,
    };

    this.prevKey = key;
    this.prevNodesRef = nodes;
    this.prevEdgesRef = edges;
    this.prevSettingsRef = settings;
    this.prevResult = result;

    return result;
  }

  static clearCache(): void {
    this.prevKey = null;
    this.prevResult = null;
    this.prevNodesRef = null;
    this.prevEdgesRef = null;
    this.prevSettingsRef = null;
  }
}
